import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useParams, useSearchParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, Icon, Input, Segmented, Select, useToast, type Tone } from "@agentsaas/ui";
import type { UsageRecord } from "@agentsaas/protocol";
import type { CallOutput } from "@agentsaas/client-sdk";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatDateTime, formatMicroUsd, formatNumber } from "../app/format";
import { useProjects } from "../data/queries";
import { useInvoices, useTenantAgents, useUsage, useUsageRecords, useWallet } from "../data/billing";
import { Loading } from "../layout/Loading";
import { SubFrame, TenantCrumb } from "../layout/SubFrame";
import { BarList, ColumnChart, type Datum } from "./charts";
import { fillDays, microToUsdInput, optionalUsd, rangeOf, toMinorUnits, topupArrived, type RangeDays } from "./model";

/**
 * /t/:tid/billing[/wallet|/invoices|/budget]：计费。
 * - 用量：区间（7/30/90 天）× 分组（天 / agent / 项目 / 类型）× 类型筛选；总额 + 图表（按天柱状、其余横向条形）+ 表格视图 + 最近明细
 * - 钱包：余额；充值（选 provider → 跳转链接 / 二维码 / 测试支付），轮询余额直到到账
 * - 账单；预算（契约只有 PUT，没有读取接口：上次保存的值记在本机）
 */

type Section = "usage" | "wallet" | "invoices" | "budget";
type GroupBy = "day" | "agent" | "project" | "kind";
type KindFilter = "all" | UsageRecord["kind"];
const RANGES: RangeDays[] = [7, 30, 90];
const GROUPS: GroupBy[] = ["day", "agent", "project", "kind"];
const KINDS: KindFilter[] = ["all", "llm", "media", "compute"];

export function BillingPage({ section }: { section: Section }) {
  const { tid = "" } = useParams();
  const { t } = useT();
  const items = [
    { id: "usage", to: `/t/${tid}/billing`, label: t("billing.nav.usage"), icon: "chart" as const },
    { id: "wallet", to: `/t/${tid}/billing/wallet`, label: t("billing.nav.wallet"), icon: "wallet" as const },
    { id: "invoices", to: `/t/${tid}/billing/invoices`, label: t("billing.nav.invoices"), icon: "file" as const },
    { id: "budget", to: `/t/${tid}/billing/budget`, label: t("billing.nav.budget"), icon: "shield" as const },
  ];
  return (
    <>
      <TenantCrumb tid={tid} section={t("billing.title")} />
      <SubFrame label={t("billing.title")} items={items} active={section}>
        {section === "usage" ? <UsageTab tid={tid} /> : section === "wallet" ? <WalletTab tid={tid} /> : section === "invoices" ? <InvoicesTab tid={tid} /> : <BudgetTab tid={tid} />}
      </SubFrame>
    </>
  );
}

// ───────────── 用量 ─────────────

function UsageTab({ tid }: { tid: string }) {
  const { t, locale } = useT();
  const { mock } = useServices();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const days = (RANGES.includes(Number(sp.get("range")) as RangeDays) ? Number(sp.get("range")) : 30) as RangeDays;
  const groupBy = (GROUPS.includes(sp.get("group") as GroupBy) ? sp.get("group") : "day") as GroupBy;
  const kind = (KINDS.includes(sp.get("kind") as KindFilter) ? sp.get("kind") : "all") as KindFilter;
  const [asTable, setAsTable] = useState(false);
  const set = (k: string, v: string) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        n.set(k, v);
        return n;
      },
      { replace: true },
    );
  const range = useMemo(() => rangeOf(days), [days]);
  const usage = useUsage(tid, { from: range.from, to: range.to, groupBy, ...(kind !== "all" ? { kind } : {}) });
  const agents = useTenantAgents(tid);
  const projects = useProjects(tid).data ?? [];
  const money = (m: number) => formatMicroUsd(m, locale);
  const dayLabel = (key: string) => formatDateTime(`${key}T12:00:00Z`, locale, "UTC", { month: "short", day: "numeric" });
  const labelOf = (key: string) => {
    if (groupBy === "day") return dayLabel(key);
    if (key === "none") return t("billing.usage.unattributed");
    if (groupBy === "kind") return t(`billing.kind.${key}`);
    if (groupBy === "agent") return agents.find((a) => a.id === key)?.name ?? key;
    return projects.find((p) => p.id === key)?.name ?? key;
  };
  const rows = usage.data ? (groupBy === "day" ? fillDays(usage.data.rows, range.fromDay, range.toDay) : usage.data.rows) : [];
  const data: Datum[] = rows.map((r) => ({ key: r.key, label: labelOf(r.key), value: r.costMicroUsd, valueText: money(r.costMicroUsd) }));
  const chartLabel = t("billing.usage.chart_label", { group: t(`billing.group.${groupBy}`), days });

  return (
    <div className="stack">
      <div className="page__head">
        <h1 className="page__title">{t("billing.nav.usage")}</h1>
        {mock ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              mock.billing.seedUsage(tid);
              void qc.invalidateQueries({ queryKey: ["billing", tid] });
            }}
            data-testid="dev-usage"
          >
            {t("billing.dev_seed")}
          </Button>
        ) : null}
      </div>
      <div className="filters row usage-filters">
        <Segmented<string> label={t("billing.usage.range")} value={String(days)} onChange={(v) => set("range", v)} options={RANGES.map((d) => ({ value: String(d), label: t("billing.usage.days", { days: d }) }))} />
        <Segmented<GroupBy> label={t("billing.usage.group_by")} value={groupBy} onChange={(v) => set("group", v)} options={GROUPS.map((g) => ({ value: g, label: t(`billing.group.${g}`) }))} />
        <Select aria-label={t("billing.usage.kind")} value={kind} onChange={(e) => set("kind", e.target.value)} className="usage-kind" data-testid="usage-kind">
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {k === "all" ? t("billing.kind.all") : t(`billing.kind.${k}`)}
            </option>
          ))}
        </Select>
      </div>
      <section className="card stack" aria-labelledby="usage-total-label">
        <div className="row">
          <div className="grow stat">
            <span id="usage-total-label" className="small muted">
              {t("billing.usage.total", { days })}
            </span>
            <strong className="stat__value num" data-testid="usage-total">
              {usage.data ? money(usage.data.totalMicroUsd) : "—"}
            </strong>
          </div>
          <Button size="sm" variant="ghost" icon={asTable ? "chart" : "list"} onClick={() => setAsTable(!asTable)} aria-pressed={asTable} data-testid="usage-toggle-table">
            {asTable ? t("billing.usage.show_chart") : t("billing.usage.show_table")}
          </Button>
        </div>
        {usage.isPending ? (
          <Loading />
        ) : !rows.length || usage.data?.totalMicroUsd === 0 ? (
          <EmptyState icon="chart" title={t("billing.usage.empty")} />
        ) : asTable ? (
          <div className="table-wrap">
            <table className="table" data-testid="usage-table">
              <caption className="ui-sr-only">{chartLabel}</caption>
              <thead>
                <tr>
                  <th scope="col">{t(`billing.group.${groupBy}`)}</th>
                  <th scope="col" className="num">
                    {t("billing.usage.amount")}
                  </th>
                  <th scope="col" className="num">
                    {t("billing.usage.cost")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}>
                    <th scope="row">{labelOf(r.key)}</th>
                    <td className="num">{r.unit && r.unit !== "mixed" ? t("billing.usage.amount_unit", { amount: formatNumber(r.amount, locale), unit: r.unit }) : "—"}</td>
                    <td className="num">{money(r.costMicroUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : groupBy === "day" ? (
          <ColumnChart data={data} label={chartLabel} axisText={(v) => formatMicroUsd(v, locale)} />
        ) : (
          <BarList data={data} label={chartLabel} />
        )}
      </section>
      <RecentRecords tid={tid} labelOfAgent={(id) => agents.find((a) => a.id === id)?.name ?? id} />
    </div>
  );
}

function RecentRecords({ tid, labelOfAgent }: { tid: string; labelOfAgent(id: string): string }) {
  const { t, locale } = useT();
  const tz = useSession().state.user?.timezone;
  const recs = useUsageRecords(tid);
  if (!recs.data?.length) return null;
  return (
    <section className="stack">
      <h2 className="section-title">{t("billing.usage.recent")}</h2>
      <div className="table-wrap card">
        <table className="table table--stack" data-testid="usage-records">
          <thead>
            <tr>
              <th scope="col">{t("billing.usage.time")}</th>
              <th scope="col">{t("billing.group.agent")}</th>
              <th scope="col">{t("billing.group.kind")}</th>
              <th scope="col" className="num">
                {t("billing.usage.amount")}
              </th>
              <th scope="col" className="num">
                {t("billing.usage.cost")}
              </th>
            </tr>
          </thead>
          <tbody>
            {recs.data.map((r, i) => (
              <tr key={i}>
                <td>
                  <time dateTime={r.at}>{formatDateTime(r.at, locale, tz)}</time>
                </td>
                <td>{r.agentId ? labelOfAgent(r.agentId) : t("billing.usage.unattributed")}</td>
                <td>{t(`billing.kind.${r.kind}`)}</td>
                <td className="num">{t("billing.usage.amount_unit", { amount: formatNumber(r.amount, locale), unit: r.unit })}</td>
                <td className="num">{formatMicroUsd(r.costMicroUsd, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ───────────── 钱包 ─────────────

type Provider = "stripe" | "alipay" | "wechat" | "fake";
type TopupResult = CallOutput<"walletTopup">;

function WalletTab({ tid }: { tid: string }) {
  const { t, locale } = useT();
  const tz = useSession().state.user?.timezone;
  const toast = useToast();
  const [open, setOpen] = useState(false);
  /** 等待到账：充值前的余额 */
  const [waitingFrom, setWaitingFrom] = useState<number | null>(null);
  const wallet = useWallet(tid, waitingFrom !== null ? 1500 : false);
  const balance = wallet.data?.balanceMicroUsd;

  useEffect(() => {
    if (waitingFrom === null || !topupArrived(waitingFrom, balance)) return;
    toast.show({ message: t("billing.wallet.arrived", { amount: formatMicroUsd(balance! - waitingFrom, locale) }), tone: "success" });
    setWaitingFrom(null);
    setOpen(false);
  }, [balance, waitingFrom, toast, t, locale]);

  // 最多等 3 分钟
  useEffect(() => {
    if (waitingFrom === null) return;
    const h = setTimeout(() => setWaitingFrom(null), 180_000);
    return () => clearTimeout(h);
  }, [waitingFrom]);

  return (
    <div className="stack">
      <div className="page__head">
        <h1 className="page__title">{t("billing.nav.wallet")}</h1>
        <Button variant="primary" icon="plus" onClick={() => setOpen(true)} data-testid="topup">
          {t("billing.wallet.topup")}
        </Button>
      </div>
      <section className="card stat stat--hero" aria-live="polite">
        <span className="small muted">{t("billing.wallet.balance")}</span>
        {wallet.isPending ? (
          <Loading />
        ) : wallet.data ? (
          <>
            <strong className={`stat__value stat__value--hero num${wallet.data.balanceMicroUsd < 0 ? " is-negative" : ""}`} data-testid="wallet-balance">
              {formatMicroUsd(wallet.data.balanceMicroUsd, locale)}
            </strong>
            <span className="small muted">{t("billing.wallet.updated", { time: formatDateTime(wallet.data.updatedAt, locale, tz) })}</span>
          </>
        ) : null}
        {waitingFrom !== null ? (
          <span className="row small" role="status" data-testid="topup-waiting">
            <span className="typing-dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
            {t("billing.wallet.waiting")}
          </span>
        ) : null}
      </section>
      <TopupDialog
        tid={tid}
        open={open}
        waiting={waitingFrom !== null}
        onClose={() => setOpen(false)}
        onStarted={() => setWaitingFrom(balance ?? 0)}
      />
    </div>
  );
}

function TopupDialog({ tid, open, waiting, onClose, onStarted }: { tid: string; open: boolean; waiting: boolean; onClose(): void; onStarted(): void }) {
  const { t } = useT();
  const errText = useErrorText();
  const { api, config } = useServices();
  const providers: Provider[] = config.mock ? ["fake", "stripe", "alipay", "wechat"] : ["stripe", "alipay", "wechat"];
  const [amount, setAmount] = useState("10");
  const [currency, setCurrency] = useState<"USD" | "CNY">("USD");
  const [provider, setProvider] = useState<Provider>(providers[0]!);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TopupResult | null>(null);

  useEffect(() => {
    if (!open) {
      setResult(null);
      setError(null);
    }
  }, [open]);

  const minor = toMinorUnits(amount);
  const pickProvider = (p: Provider) => {
    setProvider(p);
    // 支付宝 / 微信只收人民币
    if (p === "alipay" || p === "wechat") setCurrency("CNY");
  };
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!minor) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.call("walletTopup", { params: { tid }, body: { amount: minor, currency, provider } });
      setResult(r);
      onStarted();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("billing.wallet.topup")}
      closeLabel={t("shell.close")}
      footer={
        result ? (
          <Button onClick={onClose}>{t("admin.done")}</Button>
        ) : (
          <>
            <Button onClick={onClose}>{t("common.cancel")}</Button>
            <Button variant="primary" loading={busy} disabled={!minor} onClick={() => void submit()} data-testid="topup-submit">
              {t("billing.wallet.pay")}
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="stack topup-result" data-testid="topup-result">
          {result.redirectUrl ? (
            <>
              <p>{t("billing.wallet.redirect_hint")}</p>
              <a href={result.redirectUrl} target="_blank" rel="noopener noreferrer" className="ui-btn ui-btn--primary ui-btn--md" data-testid="topup-redirect">
                {t("billing.wallet.continue")}
                <Icon name="chevronRight" size={16} />
              </a>
            </>
          ) : null}
          {result.qrCode ? (
            <>
              <p>{t(`billing.wallet.scan.${provider}`)}</p>
              {/^(data:image\/|https?:)/.test(result.qrCode) ? (
                <img src={result.qrCode} alt={t("billing.wallet.qr_alt")} className="topup-qr" width={200} height={200} data-testid="topup-qr" />
              ) : (
                <pre className="codeblock mono small" data-testid="topup-qr">
                  {result.qrCode}
                </pre>
              )}
            </>
          ) : null}
          {!result.redirectUrl && !result.qrCode ? <p>{t("billing.wallet.processing")}</p> : null}
          <p className="small muted">
            {waiting ? t("billing.wallet.waiting") : t("billing.wallet.check_balance")}
            <span className="mono">{result.paymentId}</span>
          </p>
        </div>
      ) : (
        <form onSubmit={submit}>
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          <fieldset>
            <legend className="ui-field__label">{t("billing.wallet.provider")}</legend>
            <div className="provider-grid">
              {providers.map((p) => (
                <label key={p} className={`provider${provider === p ? " is-active" : ""}`}>
                  <input type="radio" name="provider" value={p} checked={provider === p} onChange={() => pickProvider(p)} data-testid={`provider-${p}`} />
                  <span>{t(`billing.provider.${p}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <div className="form-grid">
            <Field label={t("billing.wallet.amount")} error={amount.trim() && !minor ? t("billing.wallet.amount_invalid") : undefined}>
              {(id) => <Input id={id} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="topup-amount" />}
            </Field>
            <Field label={t("billing.wallet.currency")}>
              {(id) => (
                <Select id={id} value={currency} onChange={(e) => setCurrency(e.target.value as "USD" | "CNY")} data-testid="topup-currency">
                  <option value="USD">{t("billing.currency.USD")}</option>
                  <option value="CNY">{t("billing.currency.CNY")}</option>
                </Select>
              )}
            </Field>
          </div>
        </form>
      )}
    </Dialog>
  );
}

// ───────────── 账单 ─────────────

const INVOICE_TONE: Record<"open" | "paid" | "void", Tone> = { open: "warning", paid: "success", void: "neutral" };

function InvoicesTab({ tid }: { tid: string }) {
  const { t, locale } = useT();
  const tz = useSession().state.user?.timezone;
  const invoices = useInvoices(tid);
  const period = (p: string) => formatDateTime(`${p}-15T12:00:00Z`, locale, "UTC", { year: "numeric", month: "long" });
  return (
    <div className="stack">
      <div className="page__head">
        <h1 className="page__title">{t("billing.nav.invoices")}</h1>
      </div>
      {invoices.isPending ? (
        <Loading />
      ) : invoices.data?.length ? (
        <div className="table-wrap card">
          <table className="table table--stack" data-testid="invoice-table">
            <thead>
              <tr>
                <th scope="col">{t("billing.invoice.period")}</th>
                <th scope="col" className="num">
                  {t("billing.invoice.total")}
                </th>
                <th scope="col">{t("billing.invoice.status")}</th>
                <th scope="col">{t("billing.invoice.created")}</th>
              </tr>
            </thead>
            <tbody>
              {invoices.data.map((inv) => (
                <tr key={inv.id} data-testid="invoice-row">
                  <th scope="row">{period(inv.period)}</th>
                  <td className="num">{inv.currency === "USD" ? formatMicroUsd(inv.totalMicroUsd, locale) : formatNumber(inv.totalMicroUsd / 1_000_000, locale, { style: "currency", currency: inv.currency })}</td>
                  <td>
                    <Badge tone={INVOICE_TONE[inv.status]}>{t(`billing.invoice.state.${inv.status}`)}</Badge>
                  </td>
                  <td>{formatDateTime(inv.createdAt, locale, tz, { dateStyle: "medium" })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState icon="file" title={t("billing.invoice.empty")} />
      )}
    </div>
  );
}

// ───────────── 预算 ─────────────

const BUDGET_KEY = (tid: string) => `as.budget.${tid}`;
type BudgetValue = CallOutput<"budgetPut">;

function readBudget(tid: string): BudgetValue | null {
  try {
    const raw = localStorage.getItem(BUDGET_KEY(tid));
    return raw ? (JSON.parse(raw) as BudgetValue) : null;
  } catch {
    return null;
  }
}

function BudgetTab({ tid }: { tid: string }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const saved = readBudget(tid);
  const [monthly, setMonthly] = useState(microToUsdInput(saved?.monthlyLimitMicroUsd ?? null));
  const [perAgent, setPerAgent] = useState(microToUsdInput(saved?.perAgentDefaultMicroUsd ?? null));
  const [alert, setAlert] = useState(String(saved?.alertAtPercent ?? 80));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const m = optionalUsd(monthly);
  const a = optionalUsd(perAgent);
  const pct = Number(alert);
  const pctOk = Number.isInteger(pct) && pct >= 1 && pct <= 100;
  const valid = m !== undefined && a !== undefined && pctOk;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.call("budgetPut", { params: { tid }, body: { monthlyLimitMicroUsd: m, perAgentDefaultMicroUsd: a, alertAtPercent: pct } });
      try {
        localStorage.setItem(BUDGET_KEY(tid), JSON.stringify(r));
      } catch {
        /* ignore */
      }
      toast.show({ message: t("billing.budget.saved"), tone: "success" });
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="stack">
      <div className="page__head">
        <h1 className="page__title">{t("billing.nav.budget")}</h1>
      </div>
      <form className="card budget-form" onSubmit={submit} data-testid="budget-form">
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <Field label={t("billing.budget.monthly")} hint={t("billing.budget.blank_unlimited")} error={m === undefined ? t("billing.budget.invalid") : undefined}>
          {(id, d) => <Input id={id} aria-describedby={d} inputMode="decimal" value={monthly} onChange={(e) => setMonthly(e.target.value)} data-testid="budget-monthly" />}
        </Field>
        <Field label={t("billing.budget.per_agent")} hint={t("billing.budget.per_agent_hint")} error={a === undefined ? t("billing.budget.invalid") : undefined}>
          {(id, d) => <Input id={id} aria-describedby={d} inputMode="decimal" value={perAgent} onChange={(e) => setPerAgent(e.target.value)} data-testid="budget-agent" />}
        </Field>
        <Field label={t("billing.budget.alert")} hint={t("billing.budget.alert_hint")} error={pctOk ? undefined : t("billing.budget.alert_invalid")}>
          {(id, d) => <Input id={id} aria-describedby={d} type="number" min={1} max={100} inputMode="numeric" value={alert} onChange={(e) => setAlert(e.target.value)} data-testid="budget-alert" />}
        </Field>
        {saved ? null : <p className="small muted">{t("billing.budget.unknown")}</p>}
        <Button type="submit" variant="primary" loading={busy} disabled={!valid} data-testid="budget-save">
          {t("common.save")}
        </Button>
      </form>
    </div>
  );
}

